import type { CrmFilterKey } from "app/pages/sales-intelligence/domain/crm-integration/types/crm-filter-key";
import type { SignalTypeKey } from "app/pages/sales-intelligence/domain/signals/types/signal-key";

import type { ILeadsSearchFiltersDto, LeadsSearchFilterKey } from "./leadsSearchFilters.dto";

/**
 * Keys claimed by an intrinsic `belongsTo` predicate in `leadsPanelToDtoTransforms` (`isSignalTypeKey`, the
 * `crm:` prefix) rather than by the routing map. They are open-ended key families, so they cannot appear in
 * a static table — and need not, since their predicate does not depend on registration either.
 */
type PredicateRoutedFilterKey = CrmFilterKey | SignalTypeKey;

/** The DTO categories that are keyed objects. The other two are claimed by predicate, never by key. */
type KeyedApiCategory = Exclude<keyof ILeadsSearchFiltersDto, "signalsFilters" | "salesforceCustomFilters">;

export type StaticLeadsFilterKey = Exclude<LeadsSearchFilterKey, PredicateRoutedFilterKey>;

/**
 * The category a key *actually* lives under, read off the DTO itself. Mapping a key to the wrong category is
 * therefore a compile error, not merely a missing one — the table cannot drift from the wire contract.
 */
type ApiCategoryHolding<K extends StaticLeadsFilterKey> = {
    [C in KeyedApiCategory]: K extends keyof ILeadsSearchFiltersDto[C] ? C : never;
}[KeyedApiCategory];

/**
 * A filter's DTO category is a fact of the wire contract, not of the live panel config.
 *
 * `filterPanelValueToDto` hoists any key it cannot place to the DTO's top level, while
 * `filterDtoToPanelValue` restores `leadsFilters` into the panel wholesale — registered or not. Deriving the
 * routing map from the registered config therefore meant a value whose filter was merely *hidden* (flag off,
 * category-scoped out, key-hidden, or the config still `pending`) was written back as a sibling of
 * `leadsFilters` instead of inside it: the backend stops seeing the filter, and the next save persists the
 * malformed shape.
 *
 * The `satisfies` below — deliberately not `Partial`, and typed per key via `ApiCategoryHolding` — is what
 * makes that unreachable: a new DTO field with no entry here is a compile error, and an entry naming the
 * wrong category is too. The panel→DTO fold is therefore total, and correct, by construction.
 */
export const leadsFilterApiCategory = {
    useContactFiltersForCompanies: "contactsFilters",
    employeeLocation: "contactsFilters",
    contactDetails: "contactsFilters",
    contactsPerCompanyFilter: "contactsFilters",
    profileIds: "contactsFilters",
    profiles: "contactsFilters",
    employeeProfiles: "contactsFilters",
    companyDomains: "contactsFilters",

    countries: "leadsFilters",
    crmScope: "leadsFilters",
    salesforce: "leadsFilters",
    hubspot: "leadsFilters",
    dynamics: "leadsFilters",
    suppressionListIds: "leadsFilters",
    staticListsToExclude: "leadsFilters",
    websiteType: "leadsFilters",
    topLevelDomain: "leadsFilters",
    annualRevenue: "leadsFilters",
    onlineRevenue: "leadsFilters",
    conversionRate: "leadsFilters",
    monthlyAverageTransactions: "leadsFilters",
    totalFundingAmount: "leadsFilters",
    usdSpend: "leadsFilters",
    incomingTraffic: "leadsFilters",
    engagement: "leadsFilters",
    marketingChannels: "leadsFilters",
    audienceAgeGroups: "leadsFilters",
    audienceGenderSplits: "leadsFilters",
    businessModels: "leadsFilters",
    leadScores: "leadsFilters",
    employeeHeadcount: "leadsFilters",
    topGeoCountries: "leadsFilters",
    internationalFilters: "leadsFilters",
    industryFilter: "leadsFilters",
    industryTaxonomy: "leadsFilters",
    industryCodesFilter: "leadsFilters",
    siteTags: "leadsFilters",
    technologies: "leadsFilters",
    companyHeadquarter: "leadsFilters",
    keywords: "leadsFilters",
    adSpendVolume: "leadsFilters",
    adSpendChannels: "leadsFilters",
    displayAdsFormat: "leadsFilters",

    market: "appsFilters",
    price: "appsFilters",
    updateDate: "appsFilters",
    releaseDate: "appsFilters",
    sdks: "appsFilters",
    listedAddress: "appsFilters",
    rating: "appsFilters",
    permissions: "appsFilters",
    appBusinessModels: "appsFilters",
    adsSellers: "appsFilters",
    iabCategories: "appsFilters",
    identifier: "appsFilters",
    category: "appsFilters",
    downloadsAndEngagement: "appsFilters",
} as const satisfies { [K in StaticLeadsFilterKey]: ApiCategoryHolding<K> };

export type ApiCategoryOf<K extends StaticLeadsFilterKey> = (typeof leadsFilterApiCategory)[K];
